import React, { useCallback, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { useUserPermission } from '@contexts/UserContext';

import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import FeaturesDrawer from '@components/Features/FeaturesDrawer';
import MainFeatures from '@components/Features/MainFeatures';
import { NwButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * Occupa lo spazio lasciato libero dalle azioni dell'header e ospita le ombre ai bordi,
 * mostrate solo dal lato in cui c'è ancora contenuto oltre il bordo: a riposo non viene
 * dipinto nulla, quindi non serve indovinare il colore dello sfondo sottostante.
 */
const ModelFeaturesScrollArea = styled.div`
    position: relative;
    flex: ${(props) => (props.$mobileView ? 'none' : '1 1 auto')};
    min-width: 0;

    &::before,
    &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 0.6rem;
        pointer-events: none;
    }

    &::before {
        left: 0;
        background: radial-gradient(farthest-side at 0 50%, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0));
        opacity: ${(props) => (props.$scrollableLeft ? 1 : 0)};
    }

    &::after {
        right: 0;
        background: radial-gradient(farthest-side at 100% 50%, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0));
        opacity: ${(props) => (props.$scrollableRight ? 1 : 0)};
    }
`;

const ModelFeaturesScroller = styled.div`
    overflow-x: ${(props) => (props.$mobileView ? 'visible' : 'auto')};
    overflow-y: hidden;

    &::-webkit-scrollbar {
        height: 5px;
    }
`;

const ModelMainFeaturesContainer = styled.div`
    display: flex;
    cursor: pointer;
    justify-content: left;
    align-items: ${(props) => (props.$mobileView ? 'flex-start' : 'center')};
    flex-direction: ${(props) => props.$mobileView && 'column'};
    /* in orizzontale le feature scorrono, non vanno a capo */
    flex-wrap: ${(props) => (props.$mobileView ? 'wrap' : 'nowrap')};
    min-height: 3em;
    dl {
        flex: 0 0 auto;
        border-right: ${(props) => !props.$mobileView && props.theme.DefaultBorder};
        /* border-bottom: ${(props) => props.$mobileView && props.theme.DefaultBorder}; */
        padding: ${(props) => (props.$mobileView ? 0 : '.25rem .5rem')};
        margin-bottom: 0;

        display: flex;
        flex-direction: ${(props) => (props.$mobileView ? 'row' : 'column')};
        align-items: ${(props) => (props.$mobileView ? 'center' : 'flex-start')};

        &:last-of-type {
            border-right: ${(props) => !props.$hasFeatureFilter && 'none'};
        }

        &:first-of-type {
            padding-left: ${(props) => !props.$multiLine && 0};
        }

        dt {
            font-weight: normal;
            font-size: ${(props) => (props.$mobileView ? '.9rem' : '.65rem')};
            display: block;
            text-transform: uppercase;
            color: #000;
        }
        dd {
            display: block;
            font-size: 0.9rem;
            margin-bottom: ${(props) => props.$mobileView && 0};
            margin-left: ${(props) => props.$mobileView && '4px'};
            font-weight: bold;
            text-transform: uppercase;

            /* valore pubblico accanto a quello privato, come nel drawer feature */
            .feature-inline-separator {
                margin: 0 0.25rem;
                font-weight: normal;
                color: ${(props) => props.theme.TextColorMuted};
            }

            .public-feature-value {
                color: ${(props) => props.theme.PrimaryColor};
            }
        }
    }
`;

const ModelDetailsHeaderFeatures = ({ contact, readOnly, multiline, hasFeatureFilter, showHeader }) => {
    const [editFeatures, setEditFeatures] = useState(false);
    const viewModelFeatures = useUserPermission(
        'ViewModelFeatures',
        contact && contact.Users.map((user) => user.UserID)
    );
    const breakpoints = useNwBreakPoints();

    const onCloseFeatureDrawer = () => {
        //forceReloadContact(contact.ID)
        setEditFeatures(false);
    };

    const mobileView = breakpoints === 1;
    const scrollerRef = useRef(null);
    const [scrollable, setScrollable] = useState({ left: false, right: false });

    // un lato è "scorribile" se da quella parte resta contenuto fuori dal bordo
    const updateScrollable = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) {
            return;
        }
        const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
        const left = scroller.scrollLeft > 1;
        const right = scroller.scrollLeft < maxScrollLeft - 1;
        // lo scroll emette molti eventi: si aggiorna solo quando i due lati cambiano davvero
        setScrollable((current) => (current.left === left && current.right === right ? current : { left, right }));
    }, []);

    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller || mobileView) {
            setScrollable({ left: false, right: false });
            return;
        }
        updateScrollable();
        // la larghezza disponibile cambia col layout dell'header, non solo col resize della finestra
        const observer = new ResizeObserver(updateScrollable);
        observer.observe(scroller);
        return () => observer.disconnect();
    }, [mobileView, contact, updateScrollable]);

    return (
        <ModelFeaturesScrollArea
            $mobileView={mobileView}
            $scrollableLeft={scrollable.left}
            $scrollableRight={scrollable.right}
        >
            {showHeader && viewModelFeatures && (
                <button type="button" className="features-header" onClick={() => setEditFeatures(true)}>
                    <span>Features</span>
                    <NwIcon className="features-edit" icon={byPrefixAndName.fasl['pen']} />
                </button>
            )}
            <ModelFeaturesScroller
                ref={scrollerRef}
                className="features-content"
                $mobileView={mobileView}
                onScroll={updateScrollable}
            >
                <PermissionBlock
                    permission={viewModelFeatures}
                    content={
                        <ModelMainFeaturesContainer
                            $mobileView={mobileView}
                            $hasFeatureFilter={hasFeatureFilter}
                            $multiLine={multiline}
                            onClick={() => viewModelFeatures && setEditFeatures(true)}
                        >
                            <MainFeatures
                                contact={contact}
                                showPublicValues
                                /* nell'header non serve il limite: in orizzontale si scorre, in verticale si impila */
                                showAllFeatures
                                empty={
                                    readOnly ? (
                                        'no features'
                                    ) : (
                                        <NwButton
                                            style={{ opacity: 0.4 }}
                                            ghost
                                            icon={byPrefixAndName.fasl['pen']}
                                            size="lg"
                                            onClick={() => setEditFeatures(true)}
                                            label="edit measurements and sizes"
                                        />
                                    )
                                }
                            />
                        </ModelMainFeaturesContainer>
                    }
                />
            </ModelFeaturesScroller>
            {editFeatures && <FeaturesDrawer contact={contact} readOnly={readOnly} onClose={onCloseFeatureDrawer} />}
        </ModelFeaturesScrollArea>
    );
};

export default ModelDetailsHeaderFeatures;
